import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type AutoModalRequest = {
  id: symbol;
  priority: number;
  order: number;
};

const selectAutoModal = (
  requests: AutoModalRequest[],
  activeId: symbol | null
) => {
  if (activeId && requests.some(request => request.id === activeId)) {
    return activeId;
  }

  return (
    [...requests].sort(
      (a, b) => b.priority - a.priority || a.order - b.order
    )[0]?.id ?? null
  );
};

type Queue = {
  register: (id: symbol, priority: number) => () => void;
  suspend: (id: symbol) => () => void;
  activeId: symbol | null;
};

const AutoModalContext = createContext<Queue | null>(null);

export const AutoModalPriority = {
  Reminder: 10,
  Status: 20,
  Verification: 30,
  Transaction: 40,
} as const;

export function AutoModalProvider({ children }: { children: React.ReactNode }) {
  const [requests, setRequests] = useState<AutoModalRequest[]>([]);
  const [activeId, setActiveId] = useState<symbol | null>(null);
  const [suspensions, setSuspensions] = useState<symbol[]>([]);
  const order = useRef(0);

  const selectedId = selectAutoModal(requests, activeId);

  useEffect(() => {
    setActiveId(selectedId);
  }, [selectedId]);

  const register = useCallback((id: symbol, priority: number) => {
    setRequests(current => [
      ...current.filter(request => request.id !== id),
      { id, priority, order: order.current++ },
    ]);
    return () => {
      setRequests(current => current.filter(request => request.id !== id));
    };
  }, []);

  const suspend = useCallback((id: symbol) => {
    setSuspensions(current => [...current, id]);
    return () => {
      setSuspensions(current =>
        current.filter(suspension => suspension !== id)
      );
    };
  }, []);

  const value = useMemo<Queue>(
    () => ({
      register,
      suspend,
      activeId: suspensions.length ? null : selectedId,
    }),
    [register, suspend, selectedId, suspensions.length]
  );

  return (
    <AutoModalContext.Provider value={value}>
      {children}
    </AutoModalContext.Provider>
  );
}

export function useAutoModalSuspension(suspended: boolean) {
  const queue = useContext(AutoModalContext);
  if (!queue)
    throw new Error('useAutoModalSuspension requires AutoModalProvider');

  const id = useRef(Symbol('auto-modal-suspension')).current;
  const { suspend } = queue;
  useLayoutEffect(() => {
    if (suspended) return suspend(id);
    return undefined;
  }, [id, suspend, suspended]);
}

export function useAutoModal(priority: number, requested: boolean) {
  const queue = useContext(AutoModalContext);
  if (!queue) throw new Error('useAutoModal requires AutoModalProvider');

  const id = useRef(Symbol('auto-modal')).current;
  const { register, activeId } = queue;
  useEffect(() => {
    if (requested) return register(id, priority);
    return undefined;
  }, [id, priority, register, requested]);

  return requested && activeId === id;
}
